import React, { useState, Fragment, useEffect } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'
import { useDispatch } from 'react-redux'
import { setFilters } from '@/store/slices/speakersSlice'
import classNames from 'classnames'
import { BiSearch } from 'react-icons/bi'
import { Popover, Transition } from '@headlessui/react'
import Highlighter from 'react-highlight-words'
import TextInput from '@/components/common/TextInput'
import Loader from '@/components/common/Loader'
import styles from '@/styles/components/navbar.module.scss'
import useDebounce from '@/hooks/useDebounce'
import { request } from '@/utils/axiosUtil'

const Search = ({ storyStyle = '' }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [searchText, setSearchText] = useState('')
  const [viewText, setViewText] = useState('')
  const [suggestions, setSuggestions] = useState({
    nameList: [],
    categoryList: [],
    speakerTypeList: [],
  })
  const [show, setShow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [speakersDetails, setSpeakersDetails] = useState(['Loading...'])
  const [error, setError] = useState('Enter at least 2 characters to search')

  const MouseOver = (value) => validateInput(searchText) && setViewText(value)

  const MouseOut = () => setViewText('')

  const validateInput = (text) => {
    if (!text || text.length < 2) {
      setError('Enter at least 2 characters to search')
      return false
    } else {
      error && setError('')
      return true
    }
  }

  const onInputChange = (e) => {
    const searchString = e.target.value
    if (searchString.length >= 2) {
      setSpeakersDetails(['Loading...'])
    }
    viewText && setViewText('')
    setSearchText(e.target.value)
    validateInput(e.target.value)
  }

  const getSuggestions = (speakers) => {
    const result = {
      nameList: [],
      categoryList: [],
      speakerTypeList: [],
    }

    speakers.forEach((item = {}) => {
      const { id = null, contentType = null, name = null, slug = null, assets = {} } = item
      switch (contentType) {
        case 'speaker':
          result.nameList.push({
            name,
            slug,
            image_url: assets?.fields?.file?.url,
          })
          break
        case 'speakerTopic':
          result.categoryList.push({
            id,
            slug,
            category: name,
          })
          break
        case 'speakerTypes':
          result.speakerTypeList.push({
            id,
            slug,
            type: name,
          })
          break
        default:
          break
      }
    })
    return result
  }

  const onClickRedirect = (type, slug) => router.push({ pathname: `/speakers/${type}/${slug}` })

  const onSearchRedirect = () => {
    setViewText('')
    if (validateInput(searchText)) {
      dispatch(
        setFilters({
          categories: null,
          searchQuery: '',
          types: null,
          profileCategory: null,
          relatedSpeakers: null,
          sortOrder: null,
        })
      )
      router.push({
        pathname: '/speakers',
        query: { query: searchText.trim() },
      })
    }
  }

  useEffect(() => {
    let tempSuggestions = { nameList: [], categoryList: [], speakerTypeList: [] }
    let tempShow = !searchText

    if (validateInput(searchText)) {
      if (speakersDetails?.length > 0) {
        tempSuggestions = getSuggestions(speakersDetails)
        tempShow = !(
          tempSuggestions?.nameList?.length === 0 &&
          tempSuggestions?.categoryList?.length === 0 &&
          tempSuggestions?.speakerTypeList.length === 0
        )
      }
    }
    setShow(tempShow)
    setSuggestions(tempSuggestions)
  }, [searchText, speakersDetails])

  useDebounce(
    async () => {
      if (searchText?.length >= 2) {
        setSpeakersDetails(['Loading...'])
        setIsLoading(true)
        const apiUrl = `/unifiedAutoPopulate?query=${encodeURIComponent(
          searchText?.trim()?.toLowerCase()
        )}`
        const res = await request({ url: apiUrl })
        const result = res?.status == 200 ? res?.data?.data : res?.response?.data
        setSpeakersDetails(result || [])
        setIsLoading(false)
      }
    },
    8e2,
    [searchText]
  )

  return (
    <div>
      <Popover>
        {({ open }) => (
          <>
            <Popover.Button
              className={
                open ? 'flex items-center outline-none' : 'flex items-center focus:outline-1'
              }
            >
              <span className="text-sm tracking-wider tagline-font">Search</span>
              <BiSearch className="h-3.5 w-3.5 text-white ml-2" />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-400"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-90 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-90 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <div
                className={classNames(
                  styles.footerImg,
                  'absolute z-[99] inset-x-1/2 bg-black w-[99.8vw] mx-[-50vw] md:mt-6 pt-9 bg-no-repeat bg-bottom bg-contain opacity-90'
                )}
              >
                <Popover.Panel className="uta-container">
                  {({ close }) => (
                    <>
                      {
                        <>
                          <div
                            className={`${
                              show
                                ? 'relative md:flex md:items-center md:gap-6  justify-center pt-24 hidden'
                                : 'relative md:flex md:items-center m-auto md:gap-6 md:pt-24 justify-center hidden'
                            } ${storyStyle}`}
                          >
                            <TextInput
                              className="w-full px-1 py-2 pb-4 mt-1 xl:text-4xl lg:text-3xl text-2xl font-normal bg-black border-b appearance-none border-custom-gray-80 placeholder:text-custom-gray-80 focus:outline-none xl:placeholder:text-4xl lg:placeholder:text-3xl placeholder:text-2xl placeholder:font-normal placeholder:leading-loose"
                              placeHolder="Search by Speaker Name, Topic, or Type"
                              name="searchText"
                              value={viewText ? viewText : searchText}
                              onKeyPress={(event) => {
                                if (event.key === 'Enter') {
                                  onSearchRedirect()
                                  close()
                                }
                              }}
                              onChange={onInputChange}
                              autoFocus={true}
                            />

                            <Link
                              href={{
                                pathname: '/speakers',
                                query: { query: searchText.trim() },
                              }}
                              legacyBehavior
                            >
                              <div className="focus:outline-1">
                                <button
                                  className="px-4 py-4 mt-1 font-bold leading-loose tracking-wide text-white bg-black bg-opacity-100 border border-gray-200 regular-fontLight text-normal w-60 button-hover"
                                  onClick={() => {
                                    onSearchRedirect()
                                    close()
                                  }}
                                >
                                  Search
                                </button>
                              </div>
                            </Link>
                          </div>
                          <div
                            className={`${
                              show
                                ? 'relative md:flex md:items-center m-auto px-1'
                                : 'relative md:flex md:items-center m-auto md:mb-14 md:mt-2.5 px-1'
                            } ${storyStyle}`}
                          >
                            <span className="text-lg font-normal leading-7 text-custom-gray-80 regular-font">
                              {error}
                            </span>

                            {speakersDetails?.length === 0 &&
                              speakersDetails[0] !== 'Loading...' &&
                              searchText.length >= 2 && (
                                <span className="text-lg font-normal leading-7 text-[#808080] regular-font">
                                  No speakers match your search criteria
                                </span>
                              )}
                            {speakersDetails[0] == 'Loading...' && searchText.length >= 2 && (
                              <span className="text-lg font-normal leading-7 text-[#808080] regular-font">
                                {speakersDetails && speakersDetails}
                              </span>
                            )}
                          </div>
                        </>
                      }

                      <div className="mt-4">
                        {show && (
                          <>
                            {isLoading ? (
                              <Loader isNavBar={true} />
                            ) : (
                              <div
                                className={`${storyStyle} w-full md:gap-6 lg:gap-6 md:mb-6 lg:mb-14`}
                              >
                                <>
                                  {suggestions?.speakerTypeList &&
                                    suggestions?.speakerTypeList?.slice(0, 5).map((item, i) => (
                                      <div
                                        key={i}
                                        className="flex md:gap-6"
                                        aria-label="speakerType"
                                      >
                                        <div className="w-full text-4xl font-thin leading-loose bg-black hover:bg-opacity-20 hover:bg-gray-200">
                                          <a
                                            tabIndex={'0'}
                                            aria-labelledby={item}
                                            className="cursor-pointer"
                                            onClick={() => {
                                              dispatch(
                                                setFilters({
                                                  profileCategory: null,
                                                  categories: null,
                                                  searchQuery: '',
                                                  types: [item?.id],
                                                  relatedSpeakers: null,
                                                  sortOrder: null,
                                                })
                                              )
                                              close()
                                              setSearchText('')
                                              setViewText('')
                                              onClickRedirect('types', item?.slug)
                                            }}
                                            onMouseOver={() => MouseOver(item?.type)}
                                            onMouseOut={MouseOut}
                                          >
                                            <Highlighter
                                              className="mt-3 ml-4 text-4xl text-custom-gray-80 search-font"
                                              highlightClassName="relative text-white bg-inherit regular-font text-4xl"
                                              searchWords={[searchText]}
                                              autoEscape={true}
                                              textToHighlight={item?.type}
                                            />
                                          </a>
                                        </div>
                                        <div className="px-4 py-4 mt-3 text-lg font-bold leading-loose text-white w-80"></div>
                                      </div>
                                    ))}

                                  {suggestions?.categoryList &&
                                    suggestions?.categoryList?.slice(0, 5).map((item, i) => (
                                      <div key={i} className="flex md:gap-6" aria-label="Category">
                                        <div className="w-full text-4xl font-thin leading-loose bg-black hover:bg-opacity-20 hover:bg-gray-200">
                                          <a
                                            className="cursor-pointer"
                                            onClick={() => {
                                              dispatch(
                                                setFilters({
                                                  profileCategory: null,
                                                  categories: [item?.id],
                                                  searchQuery: '',
                                                  types: null,
                                                  relatedSpeakers: null,
                                                  sortOrder: null,
                                                })
                                              )
                                              close()
                                              setSearchText('')
                                              setViewText('')
                                              onClickRedirect('topics', item?.slug)
                                            }}
                                            onMouseOver={() => MouseOver(item?.category)}
                                            onMouseOut={MouseOut}
                                          >
                                            <Highlighter
                                              className="mt-3 ml-4 text-4xl text-custom-gray-80 search-font"
                                              highlightClassName="relative text-white bg-inherit regular-font text-4xl"
                                              searchWords={[searchText]}
                                              autoEscape={true}
                                              textToHighlight={item?.category}
                                            />
                                          </a>
                                        </div>
                                        <div className="px-4 py-4 mt-3 text-lg font-bold leading-loose text-white w-80"></div>
                                      </div>
                                    ))}

                                  {suggestions?.nameList &&
                                    suggestions?.nameList?.slice(0, 5).map((item, i) => (
                                      <Fragment key={i}>
                                        {item && (
                                          <div
                                            className="z-0 flex md:gap-6"
                                            aria-label="speakerName"
                                          >
                                            <div className="w-full px-2 leading-loose bg-black regular-font hover:bg-opacity-20 hover:bg-gray-200">
                                              <Link href={`/speaker/${item.slug}`} legacyBehavior>
                                                <a
                                                  onClick={() => {
                                                    close()
                                                    setSearchText('')
                                                    setViewText('')
                                                    dispatch(
                                                      setFilters({
                                                        categories: null,
                                                        searchQuery: '',
                                                        types: null,
                                                        profileCategory: null,
                                                        relatedSpeakers: null,
                                                        sortOrder: null,
                                                      })
                                                    )
                                                  }}
                                                >
                                                  <div
                                                    className="flex my-2 cursor-pointer"
                                                    onMouseOver={() => MouseOver(item?.name)}
                                                    onMouseOut={MouseOut}
                                                  >
                                                    {item?.image_url !== undefined ? (
                                                      <img
                                                        height={50}
                                                        width={50}
                                                        objectFit="cover"
                                                        objectPosition="top"
                                                        src={
                                                          'https:' +
                                                          item?.image_url +
                                                          '?w=500&h=500&q=100&fit=thumb&f=top'
                                                        }
                                                        alt=""
                                                      ></img>
                                                    ) : (
                                                      <Image
                                                        height={50}
                                                        width={50}
                                                        objectFit="cover"
                                                        objectPosition="top"
                                                        src={
                                                          'https://images.ctfassets.net/p6iqoqavudao/3lLPw0ct3NWRdllJaozgZr/238aa02d0b3a99038d1969146d7916f1/placeholder.png'
                                                        }
                                                        alt=""
                                                      />
                                                    )}

                                                    <Highlighter
                                                      className="mt-3 ml-4 text-4xl text-custom-gray-80 search-font"
                                                      highlightClassName="relative text-white bg-inherit regular-font text-4xl"
                                                      searchWords={[searchText]}
                                                      autoEscape={true}
                                                      textToHighlight={item?.name}
                                                    />
                                                  </div>
                                                </a>
                                              </Link>
                                            </div>
                                            <div className="px-4 py-4 mt-3 text-lg font-bold leading-loose text-white w-80"></div>
                                          </div>
                                        )}
                                      </Fragment>
                                    ))}
                                </>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </Popover.Panel>
              </div>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  )
}

export default React.memo(Search)
